/* global triggerOmnitureInteractions */
import _ from 'lodash';
import api from '../../../api';
import helper from '../../../helper';

const actions = {
  getCRData({
    state, getters, commit, rootState,
  }, showFirstCategory) {
    return new Promise((resolve, reject) => {
      commit('setLoadingCRData', true);
      let apiPromise;
      switch (state.GSGo.reportType) {
        case 'Locations':
          apiPromise = api.get(`CRLocations/${state.compareColumns.map(o => o.id).join()}`);
          break;
        case 'Demographics':
          apiPromise = api.get(`CRDemographics/${state.GSGo.locationId}/${state.GSGo.stratCatId}`);
          break;
        case 'Years':
          apiPromise = api.get(`CRYears/${state.GSGo.locationId}/${state.compareColumns.map(o => o.id).join()}`);
          break;
        default:
          throw new Error(`Unexpected reportType: ${state.GSGo.reportType}`);
      }
      apiPromise.then((response) => {
        const missingIdDataErrors =
          helper.checkForMissingLookupIdsInCRData(
            rootState.lku, rootState.dataTypes,
            rootState.footnotes, response.data,
          );
        const duplicateIdDataErrors =
          helper.checkForDuplicateCRDataRows(response.data);
        const dataErrors = missingIdDataErrors.concat(duplicateIdDataErrors);
        if (dataErrors.length) {
          dataErrors.forEach((error) => {
            commit('addDataError', error, { root: true });
          });
        }
        if (state.GSGo.reportType === 'Demographics') {
          const compareColumns = _(response.data).map('s2').uniq()
            .map(s2 => rootState.lku.Stratification[s2])
            .orderBy('sort', 'asc')
            .value();
          commit('setCompareColumns', compareColumns);
        }
        commit('setCRDataRaw', response.data);
        // simulate no data rows for GA, COPD for testing edge case in bug 58670:
        // commit('setCRDataRaw', _(response.data).filter(o => !(o.l === '13' && o.q === 'COPD')).value());
        if (showFirstCategory) {
          const newShowCategoryIds = _(getters.categoriesFiltered).reduce((acc, obj, index) => {
            acc[obj.id] = (index === 0);
            return acc;
          }, {});
          commit('setShowCategoryIds', newShowCategoryIds);
        }
        commit('setLoadingCRData', false);
        resolve();
      })
        .catch((err) => {
          commit('setCRDataRaw', []);
          commit('setLoadingCRData', false);
          commit('addApiError', err, { root: true });
          reject(err);
        });
    });
  },
  setBrowserUrlBarToReflectCurrentCRState({ state, rootState }, router) {
    const objForQS = {
      ...state.GSGo,
      compareColumns: _(state.compareColumns).map(obj => obj.id).value().join(),
      showCategoryIds: _.keys(_.pickBy(state.showCategoryIds, v => v)).join(),
      searchString: state.searchString,
      showReportOptions: state.flags.showReportOptions,
      ...state.reportOptionsGo,
    };
    const objForQSUpper = _.mapKeys(objForQS, (value, key) => _.upperFirst(key));

    const showDebugOption = {};
    if (rootState.flags.showDebugIds) showDebugOption.showDebugIds = rootState.flags.showDebugIds;
    const showMode = {};
    if (rootState.flags.showMode) showMode.showMode = rootState.flags.showMode;

    router.replace({
      path: '/CR',
      query: {
        ...objForQSUpper,
        ...showDebugOption,
        ...showMode,
      },
    });
  },
  setNonGSStateFromQueryStringIfValid({
    state, getters, commit, rootState,
  }) {
    const { query } = rootState.route;
    // console.log(query);
    // const { lku } = rootState;

    if (query.CompareColumns) {
      const ids = query.CompareColumns.split(',');
      // number of ids < max?
      if ((getters.compareColumnsMaxCount === 0)
        || (ids.length <= getters.compareColumnsMaxCount)) {
        // id's are all valid?
        if (_.difference(
          ids,
          _(getters.allCompareColumns).map('id').value(),
        ).length === 0) {
          const compareColumns =
            _(getters.allCompareColumns).filter(o => _.indexOf(ids, o.id) !== -1)
              .orderBy('sort', getters.compareColumnsSortOrder).value();
          commit('setCompareColumns', compareColumns);
        }
      }
    }

    if (query.SearchString) commit('setSearchString', query.SearchString);

    if (query.ShowCategoryIds !== undefined) {
      // block scope for const's is not working, so use ids1 instead of ids
      const ids1 = query.ShowCategoryIds === '' ? [] : query.ShowCategoryIds.split(',');
      // id's are all valid?
      if (_.difference(
        ids1,
        _(getters.categoriesRaw).map('id').value(),
      ).length === 0) {
        const newShowCategoryIds = _(getters.categoriesFiltered).reduce((acc, obj) => {
          acc[obj.id] = _(ids1).indexOf(obj.id) !== -1;
          return acc;
        }, {});
        commit('setShowCategoryIds', newShowCategoryIds);
      }
    }

    if (query.ShowReportOptions === 'true') commit('setShowReportOptions', true);

    // default values for reportOptionsGo
    const reportOptionsGo = {
      categoryFilterId: '',
      stratFilterId: '',
      showCI: true,
      showWN: true,
      hideMissingDataRows: false,
    };

    if (query.CategoryFilterId) {
      // valid id?
      if (_(getters.categoriesRaw).find({ id: query.CategoryFilterId })) {
        reportOptionsGo.categoryFilterId = query.CategoryFilterId;
      }
    }
    if (query.StratFilterId) {
      // valid id?
      if (_(state.crDataRaw).find({ s1: query.StratFilterId })) {
        reportOptionsGo.stratFilterId = query.StratFilterId;
      }
    }
    if (query.ShowCI === 'false') reportOptionsGo.showCI = false;
    if (query.ShowWN === 'false') reportOptionsGo.showWN = false;
    if (query.HideMissingDataRows === 'true') reportOptionsGo.hideMissingDataRows = true;
    commit('setReportOptionsGo', reportOptionsGo);
  },
  triggerInteraction({ state, rootState }, { type, exportType }) {
    let interactionString;
    const {
      locationId, reportType, stratCatId, yearId,
    } = state.GSGo;
    let compareOption = '??';
    if (reportType === 'Locations') compareOption = 'Loc';
    if (reportType === 'Demographics') {
      if (stratCatId === 'AGEGRP') compareOption = 'Age';
      if (stratCatId === 'SEX') compareOption = 'Sex';
      if (stratCatId === 'RACE') compareOption = 'Race';
    }
    if (reportType === 'Years') {
      if (yearId === 'RECENT') {
        compareOption = 'Recent';
      } else {
        compareOption = rootState.lku.Year[yearId].name;
      }
    }
    const compareColumnList = _(state.compareColumns).map(reportType === 'Years' ? 'name' : 'id').value().join('-');
    const ro = state.reportOptionsGo;

    switch (type) {
      case 'CRT':
      // global selector
        interactionString = `${type} ${compareOption},${locationId}`;
        break;
      case 'CRC':
      // add/remove compare columns
        interactionString = `${type} ${compareOption},${locationId},${compareColumnList}`;
        break;
      case 'CRO':
      // update report options
        interactionString = `${type} ${ro.categoryFilterId ? ro.categoryFilterId : 'All'},${ro.stratFilterId ? ro.stratFilterId : 'All'},${ro.showCI ? 'C' : 'U'},${ro.showWN ? 'C' : 'U'},${ro.hideMissingDataRows ? 'C' : 'U'}`;
        break;
      case 'CRE':
      // export to CSV or PDF
        interactionString = `${type} ${compareOption},${compareColumnList},${exportType}`;
        break;
      default:
        throw new Error(`Unexpected interaction type: ${type}`);
    }
    triggerOmnitureInteractions(`${process.env.InteractionIdentifier}-${interactionString}`);
  },
};

export default actions;



// WEBPACK FOOTER //
// ./src/store/modules/CR/actions.js